import { useEffect } from "react";

/**
 * Embed mode: when PrimeChart is loaded inside a partner website via
 * /hospital/<slug>?embed=1, the app hides its own chrome (logo, footer) and
 * stacks the top menu vertically so it reads as a native section of that site.
 *
 * The `?embed=1` flag only appears on the first URL, so we persist it for the
 * whole visit. When NOT embedded, nothing changes — the app renders as normal.
 */
const KEY = "pc_embed";

// Call once at startup, before first paint, so the flag is captured from the entry URL.
export const initEmbed = (): void => {
  try {
    if (new URLSearchParams(window.location.search).get("embed") === "1") {
      sessionStorage.setItem(KEY, "1");
    }
  } catch {
    /* storage may be unavailable in some embedded contexts */
  }
};

// True when the page is running inside an iframe (a partner embed).
export const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws → we are framed.
    return true;
  }
};

// Embed mode = inside an iframe, OR the optional explicit ?embed=1 override.
export const isEmbedded = (): boolean => {
  try {
    if (sessionStorage.getItem(KEY) === "1") return true;
    if (new URLSearchParams(window.location.search).get("embed") === "1") return true;
  } catch {
    /* ignore */
  }
  return isFramed();
};

// Broadcast the page's content height to the parent so a partner can size the
// iframe with no inner scrollbar. Only sends height (no sensitive data).
export const usePostHeightToParent = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled) return;
    if (window.parent === window) return;

    const post = () => {
      const height = Math.ceil(
        document.documentElement.scrollHeight || document.body.scrollHeight
      );
      try {
        window.parent.postMessage({ type: "primechart:height", height }, "*");
      } catch {
        /* ignore */
      }
    };

    post();
    const observer = new ResizeObserver(post);
    observer.observe(document.body);
    window.addEventListener("load", post);
    // Catch late/async content that ResizeObserver may miss.
    const interval = window.setInterval(post, 1000);

    return () => {
      observer.disconnect();
      window.removeEventListener("load", post);
      window.clearInterval(interval);
    };
  }, [enabled]);
};
